import { App } from "@capacitor/app";
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
} from "@capacitor/push-notifications";
import {
  LocalNotifications,
  type ActionPerformed as LocalActionPerformed,
} from "@capacitor/local-notifications";
import { buildNotificationFromUnknown } from "@utils/notifications/push-builder";
import { ROUTES } from "@utils/navigation";
import { navigateFromPush } from "@utils/navigation/app-nav-bridge";
import { getNotificationHref } from "@utils/notifications";
import { isNativePlatform } from "@lib/platform";
import { parseAppDeepLink } from "@lib/platform/app-origin";
import { closeInAppBrowser } from "@lib/platform/open-url";

export type NativePushTapHandler = (url: string) => void;

let listenerInitialized = false;

function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) return value;
  return undefined;
}

/** Read routing fields from Capacitor notification.data (APNs userInfo on iOS). */
function extractRoutingFields(data: unknown): {
  url?: string;
  entityType?: string;
  entityId?: string;
  type?: string;
} {
  if (!data || typeof data !== "object") return {};
  const record = data as Record<string, unknown>;

  return {
    url: asNonEmptyString(record.url),
    entityType: asNonEmptyString(record.entity_type),
    entityId: asNonEmptyString(record.entity_id),
    type: asNonEmptyString(record.type),
  };
}

export function notificationUrlFromPayload(
  notification: PushNotificationSchema,
): string {
  const { url, entityType, entityId, type } = extractRoutingFields(
    notification.data,
  );

  if (url?.startsWith("/")) return url;

  if (entityType && entityId) {
    return getNotificationHref(entityType, entityId);
  }

  if (type) {
    return buildNotificationFromUnknown({ data: notification.data }).url;
  }

  return ROUTES.NOTIFICATIONS;
}

function dispatchTap(rawUrl: string) {
  const path = parseAppDeepLink(rawUrl);
  if (import.meta.env.DEV) {
    console.info("[push] Notification tap → navigate", rawUrl, "→", path);
  }
  navigateFromPush(path);
}

function handleNotificationAction(event: ActionPerformed) {
  if (event.actionId && event.actionId !== "tap") return;
  dispatchTap(notificationUrlFromPayload(event.notification));
}

/**
 * Android delivers foreground pushes as a locally scheduled notification (see
 * push-native.ts), so tapping one raises this event instead of
 * `pushNotificationActionPerformed`. Without this listener such a tap is a
 * silent no-op. `extra` holds the original FCM payload, so the payload is
 * routed through the same resolver rather than duplicating the field mapping.
 */
function handleLocalNotificationAction(event: LocalActionPerformed) {
  if (event.actionId && event.actionId !== "tap") return;
  dispatchTap(
    notificationUrlFromPayload({
      data: event.notification.extra,
    } as PushNotificationSchema),
  );
}

declare global {
  interface Window {
    __komutNavigateFromPush?: (url: string) => void;
  }
}

/**
 * Attach early (PlatformProvider) so cold-start taps are not lost while auth loads.
 * Also exposes `window.__komutNavigateFromPush` for the iOS native forwarder fallback.
 */
export async function initNativePushTapListener(): Promise<void> {
  if (!isNativePlatform() || listenerInitialized) return;
  listenerInitialized = true;

  window.__komutNavigateFromPush = (url: string) => dispatchTap(url);

  await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    handleNotificationAction,
  );

  await LocalNotifications.addListener(
    "localNotificationActionPerformed",
    handleLocalNotificationAction,
  );

  await App.addListener("appUrlOpen", (event) => {
    // A link that points back into the app leaves the in-app browser presented
    // above it, so dismiss the sheet before routing or the user sees the old
    // page instead of where the link sent them.
    void closeInAppBrowser();
    dispatchTap(event.url);
  });

  const launch = await App.getLaunchUrl();
  if (launch?.url) dispatchTap(launch.url);
}
